import React from 'react';
import { Box, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CONTENT_WITH_SUB_NAV_OFFSET } from 'components/Layout/Layout';
import { MODEL_DRAW_WIDTH } from 'config';
import getStopeSelectedSceneActions, { removeIsolateFilters, resetIsolateStage } from './StopeSelectedSceneActions';
import ApexChartGraph from '../../HydraulicGradeLine/ApexChartGraph';
import { returnShortestRoute } from '../../MineModel/Route/utils';
import { Model } from '../../hooks/useModelState';
import { handleNodeSelect, toggleSpoolConstraint, SceneProps } from '../scene-utils';
import { Scene } from '../types';
import sideDrawerContentFunction from './StopeSelectedSceneSideDrawer';
import ModelDrawerTrigger from '../../ModelDrawerTrigger';
import { PCNode } from '../../MineModel/Nodes';
import { IntersectionData } from '../../MineModel/intersectionTypes';
import { getFlushSceneState, subscribeToFlushSceneState } from '../../MineModel/Flushing/flushSceneState';
import { FlushOverlayInput } from '../../HydraulicGradeLine/flushOverlay';

const TITLE = 'STOPE SELECTED SCENE';

const StopeSelectedDisplay = ({ model, props }: { model: Model; props: SceneProps }) => {
  const selectedRoute = React.useMemo(
    () => model.getSelectedRoute() ?? returnShortestRoute(model.routes || {}),
    [model.routes],
  );
  const hasHydraulicGradeLine = !!selectedRoute?.routePoints?.length;

  // The Recipes tab's scrub control publishes the flush being viewed; the chart overlays it.
  const [, forceFlushUpdate] = React.useReducer((x: number) => x + 1, 0);
  const { profile: flushProfile, airStartDistance: flushAirStartDistance, handoverDistance: flushHandoverDistance } = getFlushSceneState();
  const flushOverlay = React.useMemo<FlushOverlayInput | undefined>(
    () => (flushProfile ? { profile: flushProfile, airStartDistance: flushAirStartDistance, handoverDistance: flushHandoverDistance } : undefined),
    [flushProfile, flushAirStartDistance, flushHandoverDistance],
  );

  const [size, setSize] = React.useState({ width: 400, height: 300 });
  const [isResizing, setIsResizing] = React.useState(false);

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    const readVar = (name: string) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
    const chartTop = CONTENT_WITH_SUB_NAV_OFFSET + readVar('--stopes-toolbar-height');
    const drawerOffset = props.context.state.drawerOpen ? (readVar('--stopes-drawer-width') || MODEL_DRAW_WIDTH) : 80;
    const newWidth = window.innerWidth - e.clientX - drawerOffset;
    const newHeight = e.clientY - chartTop;
    const maxWidth = window.innerWidth - drawerOffset - 100;
    const maxHeight = window.innerHeight - chartTop - 20;
    setSize({
      width: Math.max(300, Math.min(newWidth, maxWidth)),
      height: Math.max(200, Math.min(newHeight, maxHeight)),
    });
  }, [props.context.state.drawerOpen]);

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  React.useEffect(() => {
    if (!isResizing) return undefined;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  React.useEffect(() => subscribeToFlushSceneState(() => forceFlushUpdate()), []);

  return (
    <>
      {!props.context.state.drawerOpen && (
        <Box
          sx={{ position: 'absolute', right: 2, zIndex: 20 }}
        >
          <ModelDrawerTrigger handleClick={() => props.context.actions.setDrawerOpen(true)} />
        </Box>
      )}
      {hasHydraulicGradeLine && (
        <Box
          sx={{
            position: 'fixed',
            top: `calc(${CONTENT_WITH_SUB_NAV_OFFSET}px + var(--stopes-toolbar-height, 0px))`,
            right: props.context.state.drawerOpen ? `var(--stopes-drawer-width, ${MODEL_DRAW_WIDTH}px)` : 80,
            width: `${size.width}px`,
            height: `${size.height}px`,
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <IconButton
            onMouseDown={handleMouseDown}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              cursor: 'nwse-resize',
              zIndex: 3,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            size="small"
          >
            <DragIndicatorIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>
          <ApexChartGraph
            selectedRoute={selectedRoute}
            unitSystemPreference={model.project.unitSystemPreference}
            customUnitFields={model.project.customUnitFields}
            recipeState={model.recipeState ?? {}}
            optimiserResults={model.optimiserResults}
            showMinMaxOnGraph={model.project.showMinMaxOnGraph}
            showPressureLineOnHgl={model.project.showPressureLineOnHgl}
            pipeRatingOffsetPercent={model.project.showPipeRatingOffsetOnGraph ? model.recipeState?.pipeRatingOffsetPercent : undefined}
            metricPressureUnit={model.project.metricPressureUnit}
            flushOverlay={flushOverlay}
            rheologyDataset={model.rheology.find(({ rheologyDataSetId }) => rheologyDataSetId === model.recipeState?.rheologyDataSetId)}
          />
        </Box>
      )}
    </>
  );
};

const stopeSelectedScene = (props: SceneProps): Scene => {
  const display = (model: Model) => <StopeSelectedDisplay model={model} props={props} />;

  const getCanvasHandlers = (model: Model) => ({
    onNodeHover: () => { },
    onNodeSelect: (node: PCNode, stopeId?: string) => {
      handleNodeSelect(model, node, props, stopeId);
    },
    onSpoolSelect: () => { },
    onIntersections: (intersections: IntersectionData[]) => {
      const spoolIntersection = intersections.find((i) => i.type === 'spool' && i.spool);
      if (spoolIntersection?.spool) {
        const spoolOnAnyRoute = Object.values(model.routes || {}).some(
          (route) => route?.spools?.some((s) => s.spoolId === spoolIntersection.spool!.spoolId),
        );
        if (spoolOnAnyRoute) {
          toggleSpoolConstraint(model, spoolIntersection.spool);
          return;
        }
      }

      const nodeIntersection = intersections.find((i) => i.type === 'node' && i.node);
      if (nodeIntersection?.node) {
        handleNodeSelect(model, nodeIntersection.node, props);
      }
    },
  });

  const sideDrawerContent = (model: Model) => (
    sideDrawerContentFunction(
      props,
      model,
    )
  );

  const onExit = (model: Model) => {
    removeIsolateFilters(model);
    resetIsolateStage();
  };

  return {
    key: props.key,
    display,
    getCanvasHandlers,
    sideDrawerContent,
    onExit,
    title: TITLE,
    actionButtons: getStopeSelectedSceneActions(),
  };
};

export default stopeSelectedScene;
